// src/screens/Layout.js
import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  LayoutDashboard, Sparkles, Users, BrainCircuit, Mail,
  Target, Info, DollarSign, Activity, Link2, Settings,
  Menu, X, BookOpen, LayoutGrid,
} from 'lucide-react';
import './Layout.css';
import Logo from "../img/logo.png";

const ComingSoonBadge = () => (
  <span style={{
    marginLeft: 'auto',
    fontSize: '10px',
    fontWeight: 700,
    color: '#ffffff',
    background: '#808080',
    border: '1px solid #808080',
    borderRadius: '999px',
    padding: '1px 6px',
    whiteSpace: 'nowrap',
    flexShrink: 0,
  }}>
    Coming soon
  </span>
);

const DisabledNavItem = ({ icon: Icon, label }) => (
  <div
    className="nav-link nav-link-disabled"
    style={{
      display: 'flex',
      alignItems: 'center',
      gap: '10px',
      opacity: 0.45,
      cursor: 'not-allowed',
      pointerEvents: 'none',
      position: 'relative',
    }}
  >
    <Icon size={20} strokeWidth={1.5} />
    <span>{label}</span>
    <ComingSoonBadge />
  </div>
);

const Layout = ({ children }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [authChecked, setAuthChecked] = useState(false);

  const userMenuRef = useRef(null);

  const publicPaths = ['/', '/auth', '/oauth-callback'];

  const clearAuthStorage = () => {
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('token_type');
    localStorage.removeItem('user_id');
    localStorage.removeItem('user_email');
    localStorage.removeItem('role');
  };

  const getStoredToken = () => {
    return localStorage.getItem('access_token') || '';
  };

  const isTokenValid = () => {
    const token = getStoredToken();
    if (!token) return false;

    try {
      const parts = token.split('.');
      if (parts.length !== 3) return true;

      const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
      const payload = JSON.parse(atob(base64));

      if (!payload?.exp) return true;

      const now = Math.floor(Date.now() / 1000);
      return payload.exp > now;
    } catch (err) {
      console.warn('Token validation failed:', err);
      return false;
    }
  };

  useEffect(() => {
    const isPublicPage = publicPaths.includes(location.pathname);

    if (isPublicPage) {
      setAuthChecked(true);
      return;
    }

    const token = getStoredToken();
    const tokenValid = isTokenValid();
    const hasAuthenticatedUser = !!user || !!localStorage.getItem('user_id');

    if (!token || !tokenValid || !hasAuthenticatedUser) {
      clearAuthStorage();
      logout?.();
      setAuthChecked(true);
      navigate('/auth', {
        replace: true,
        state: { from: location.pathname },
      });
      return;
    }

    setAuthChecked(true);
  }, [location.pathname, navigate, logout, user]);

  useEffect(() => {
    if (!authChecked) return;

    const token = getStoredToken();
    if (!token) return;

    const resolveRole = async () => {
      const roleFromContext = user?.role;
      const roleFromStorage = localStorage.getItem('role');

      if (roleFromContext || roleFromStorage) {
        const role = (roleFromContext || roleFromStorage).toLowerCase();
        setIsAdmin(role === 'admin');
        return;
      }

      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        const userId =
          localStorage.getItem('user_id') ||
          payload?.user_id ||
          payload?.sub;

        if (!userId) return;

        const res = await fetch(
          `https://revexia.ai/api/users/${encodeURIComponent(userId)}/role`,
          { headers: { Authorization: `Bearer ${token}` } }
        );

        if (!res.ok) return;

        const data = await res.json();
        const fetchedRole = (data?.role || '').toLowerCase();

        localStorage.setItem('role', fetchedRole);
        setIsAdmin(fetchedRole === 'admin');
      } catch (err) {
        console.error('Role resolve error:', err);
      }
    };

    resolveRole();
  }, [user, authChecked]);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 992);
      if (window.innerWidth >= 992) setSidebarOpen(false);
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target)) {
        setShowUserMenu(false);
      }
    };

    if (showUserMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showUserMenu]);

  const closeSidebar = () => {
    if (isMobile) {
      setSidebarOpen(false);
      setShowUserMenu(false);
    }
  };

  const handleLogout = () => {
    clearAuthStorage();
    logout?.();
    navigate('/auth', { replace: true });
  };

  const toggleUserMenu = (e) => {
    e.stopPropagation();
    setShowUserMenu(!showUserMenu);
  };

  const getUserInitials = () => {
    if (!user) return 'AC';
    if (user.name) {
      const names = user.name.split(' ');
      return names.length > 1
        ? `${names[0][0]}${names[1][0]}`.toUpperCase()
        : names[0][0].toUpperCase();
    }
    return user.email?.[0]?.toUpperCase() || 'U';
  };

  const isActive = (path) => location.pathname === path;

  const getPageTitle = () => {
    const path = location.pathname;
    if (path === '/dashboard') return 'Dashboard';
    if (path === '/offer-engine') return 'Offers';
    if (path === '/segments') return 'Segmentation Hub';
    if (path === '/predictor') return 'Purchase Predictor';
    if (path === '/dm-activity') return 'DM Activity';
    if (path === '/attribution') return 'Attribution Map';
    if (path === '/ai-insights') return 'AI Insights';
    if (path === '/content-revenue') return 'Content Revenue';
    if (path === '/automation') return 'Follow-up Automation';
    if (path === '/integrations') return 'Integrations';
    if (path === '/settings') return 'Settings';
    if (path === '/blog/list') return 'Blog List';
    if (path === '/admin-dashboard') return 'Admin Dashboard';
    return 'Dashboard';
  };

  if (!authChecked) {
    return null;
  }

  return (
    <div className="layout-wrapper">
      {sidebarOpen && isMobile && (
        <div className="sidebar-overlay" onClick={closeSidebar}></div>
      )}

      <aside className={`left-sidebar ${sidebarOpen ? 'open' : ''}`}>
        <div className="sidebar-brand">
          <div className="brand-content">
            <Link to="/">
              <img
                src={Logo}
                alt="Revexia"
                style={{ width: '120px', height: '36px', objectFit: 'contain' }}
              />
            </Link>
          </div>
          {isMobile && (
            <button className="sidebar-close" onClick={closeSidebar}>
              <X size={20} />
            </button>
          )}
        </div>

        <nav className="sidebar-navigation">
          <div className="nav-section">
            <div className="nav-section-title">MAIN</div>

            <Link
              to="/dm-activity"
              className={`nav-link ${isActive('/dm-activity') ? 'active' : ''}`}
              onClick={closeSidebar}
            >
              <Mail size={20} strokeWidth={1.5} />
              <span>DM Activity</span>
            </Link>

            {isAdmin && <DisabledNavItem icon={LayoutDashboard} label="Dashboard" />}
            {isAdmin && <DisabledNavItem icon={Sparkles} label="Offer Engine" />}
            {isAdmin && <DisabledNavItem icon={Users} label="Segmentation Hub" />}
            {isAdmin && <DisabledNavItem icon={BrainCircuit} label="Purchase Predictor" />}
          </div>

          {isAdmin && (
            <div className="nav-section">
              <div className="nav-section-title">ANALYTICS</div>
              <DisabledNavItem icon={Target} label="Attribution Map" />
              <DisabledNavItem icon={Info} label="AI Insights" />
              <DisabledNavItem icon={DollarSign} label="Content Revenue" />
            </div>
          )}

          <div className="nav-section">
            <div className="nav-section-title">SYSTEM</div>

            {isAdmin && <DisabledNavItem icon={Activity} label="Follow-up Automation" />}

            <Link
              to="/integrations"
              className={`nav-link ${isActive('/integrations') ? 'active' : ''}`}
              onClick={closeSidebar}
            >
              <Link2 size={20} strokeWidth={1.5} />
              <span>Integrations</span>
            </Link>

            <Link
              to="/settings"
              className={`nav-link ${isActive('/settings') ? 'active' : ''}`}
              onClick={closeSidebar}
            >
              <Settings size={20} strokeWidth={1.5} />
              <span>Settings</span>
            </Link>

            {isAdmin && (
              <Link
                to="/blog/list"
                className={`nav-link ${isActive('/blog/list') ? 'active' : ''}`}
                onClick={closeSidebar}
              >
                <BookOpen size={20} strokeWidth={1.5} />
                <span>Blog List</span>
              </Link>
            )}

            {isAdmin && (
              <Link
                to="/admin-dashboard"
                className={`nav-link ${isActive('/admin-dashboard') ? 'active' : ''}`}
                onClick={closeSidebar}
              >
                <LayoutGrid size={20} strokeWidth={1.5} />
                <span>Admin Dashboard</span>
              </Link>
            )}
          </div>
        </nav>

        <div className="sidebar-user-section" ref={userMenuRef}>
          <div className="user-card" onClick={toggleUserMenu}>
            <div className="user-avatar-wrapper">
              {user?.avatar ? (
                <img src={user.avatar} alt={user.name || 'User'} className="user-avatar-img" />
              ) : (
                <div className="user-avatar-placeholder">{getUserInitials()}</div>
              )}
              <span className="user-status-dot"></span>
            </div>
            <div className="user-info">
              <div className="user-display-name">{user?.name || 'Alex Chen'}</div>
              <div className="user-display-role">{isAdmin ? 'Admin' : 'Growth Lead'}</div>
            </div>
            <svg className="user-expand-icon" width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path d="M4 6L8 10L12 6" stroke="currentColor" strokeWidth="1.5" />
            </svg>
          </div>

          {showUserMenu && (
            <div className="user-dropdown-menu">
              <Link to="/settings" className="dropdown-menu-item" onClick={closeSidebar}>
                <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                  <circle cx="9" cy="5.5" r="2.5" stroke="currentColor" strokeWidth="1.5" />
                  <path d="M3 14C3 11.5 5.5 9.5 9 9.5C12.5 9.5 15 11.5 15 14" stroke="currentColor" strokeWidth="1.5" />
                </svg>
                <span>Profile</span>
              </Link>
              <div className="dropdown-divider"></div>
              <button className="dropdown-menu-item logout-item" onClick={handleLogout}>
                <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                  <path d="M7 15H4C3.44772 15 3 14.5523 3 14V4C3 3.44772 3.44772 3 4 3H7" stroke="currentColor" strokeWidth="1.5" />
                  <path d="M12 12L15 9M15 9L12 6M15 9H7" stroke="currentColor" strokeWidth="1.5" />
                </svg>
                <span>Logout</span>
              </button>
            </div>
          )}
        </div>
      </aside>

      <div className="main-wrapper">
        <header className="top-navbar">
          <div className="navbar-left">
            {isMobile && (
              <button className="mobile-menu-btn" onClick={() => setSidebarOpen(true)}>
                <Menu size={24} />
              </button>
            )}
            <h1 className="page-title">{getPageTitle()}</h1>
          </div>
        </header>

        <div className="content-area">
          {children}
        </div>
      </div>

      <button className="floating-action-btn">
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
          <path d="M12 5V19M5 12H19" stroke="white" strokeWidth="2.5" strokeLinecap="round" />
        </svg>
      </button>
    </div>
  );
};

export default Layout;